import React, { useState } from 'react';
import { submitSystemReport } from '../services/firestore.js';
import { Send, Upload, AlertTriangle, Bug, Lightbulb, CheckCircle, X } from 'lucide-react';

export default function IssueReport({ user, onNavigate }) {
    const [reportType, setReportType] = useState('bug'); // bug, issue, suggestion
    const [description, setDescription] = useState('');
    const [reproduceSteps, setReproduceSteps] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const clearImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!description.trim()) return;

        setIsSubmitting(true);
        try {
            await submitSystemReport({
                type: reportType,
                description,
                reproduceSteps: reportType === 'bug' ? reproduceSteps : null,
                reporterId: user.uid,
                reporterName: user.name,
                reporterEmail: user.email
            }, selectedImage);

            setIsSuccess(true);
            setDescription('');
            setReproduceSteps('');
            clearImage();

            // Reset success message after 3 seconds
            setTimeout(() => {
                setIsSuccess(false);
                onNavigate('feed'); // Go back to feed or stay? User logic.
            }, 2000);

        } catch (error) {
            console.error(error);
            alert("Failed to submit report. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isSuccess) {
        return (
            <div className="glass-panel animate-fade-in" style={{ padding: '40px', textAlign: 'center', maxWidth: '600px', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                <CheckCircle size={64} color="#10b981" />
                <h2>Thank You!</h2>
                <p>Your report has been submitted to the Super Admin.</p>
                <p style={{ opacity: 0.7 }}>We value your feedback and will look into this shortly.</p>
            </div>
        );
    }

    return (
        <div className="animate-fade-in" style={{ maxWidth: '800px', margin: '0 auto', paddingBottom: '100px' }}>
            <div className="glass-panel" style={{ padding: '30px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                    <div style={{ background: 'var(--primary)', padding: '10px', borderRadius: '12px', display: 'flex' }}>
                        <AlertTriangle size={24} color="white" />
                    </div>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '24px' }}>Report an Issue</h1>
                        <p style={{ margin: 0, opacity: 0.7 }}>Found a bug? Have a suggestion? Let us know!</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                    {/* TYPE SELECTOR */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Type of Report</label>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            {[
                                { id: 'bug', label: 'Bug Report', icon: Bug, color: '#ef4444' },
                                { id: 'issue', label: 'General Issue', icon: AlertTriangle, color: '#f59e0b' },
                                { id: 'suggestion', label: 'Suggestion', icon: Lightbulb, color: '#3b82f6' }
                            ].map(type => (
                                <button
                                    key={type.id}
                                    type="button"
                                    onClick={() => setReportType(type.id)}
                                    className="btn-ghost"
                                    style={{
                                        flex: 1,
                                        padding: '15px',
                                        background: reportType === type.id ? 'rgba(255,255,255,0.1)' : 'transparent',
                                        border: reportType === type.id ? `1px solid ${type.color}` : '1px solid var(--border)',
                                        color: reportType === type.id ? 'white' : 'var(--text-muted)',
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px'
                                    }}
                                >
                                    <type.icon size={24} color={reportType === type.id ? type.color : '#666'} />
                                    <span>{type.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* DESCRIPTION */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                            {reportType === 'suggestion' ? 'What is your idea?' : 'Describe the issue'} *
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={reportType === 'suggestion' ? "I think it would be cool if..." : "I was trying to..."}
                            style={{
                                width: '100%', padding: '15px', borderRadius: '12px', border: '1px solid var(--border)',
                                background: 'rgba(0,0,0,0.2)', color: 'var(--text-main)', minHeight: '120px', fontFamily: 'inherit'
                            }}
                            required
                        />
                    </div>

                    {/* STEPS TO REPRODUCE (ONLY FOR BUGS) */}
                    {reportType === 'bug' && (
                        <div className="animate-fade-in">
                            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Steps to Reproduce</label>
                            <textarea
                                value={reproduceSteps}
                                onChange={(e) => setReproduceSteps(e.target.value)}
                                placeholder="1. Go to page X&#10;2. Click button Y&#10;3. See error Z"
                                style={{
                                    width: '100%', padding: '15px', borderRadius: '12px', border: '1px solid var(--border)',
                                    background: 'rgba(0,0,0,0.2)', color: 'var(--text-main)', minHeight: '100px', fontFamily: 'inherit'
                                }}
                            />
                        </div>
                    )}

                    {/* IMAGE UPLOAD */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Screenshot (Optional)</label>
                        {!imagePreview ? (
                            <button
                                type="button"
                                onClick={() => document.getElementById('report-image-upload').click()}
                                className="btn-ghost"
                                style={{ width: '100%', padding: '30px', border: '1px dashed var(--border)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', color: 'var(--text-muted)' }}
                            >
                                <Upload size={32} />
                                <span>Click to upload a screenshot</span>
                            </button>
                        ) : (
                            <div style={{ position: 'relative', width: 'fit-content' }}>
                                <img src={imagePreview} alt="Preview" style={{ maxHeight: '200px', borderRadius: '12px', border: '1px solid var(--border)' }} />
                                <button
                                    type="button"
                                    onClick={clearImage}
                                    style={{
                                        position: 'absolute', top: -10, right: -10,
                                        background: '#ef4444', color: 'white', border: 'none',
                                        borderRadius: '50%', width: '24px', height: '24px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                                    }}
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        )}
                        <input
                            id="report-image-upload"
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            style={{ display: 'none' }}
                        />
                    </div>

                    {/* SUBMIT BUTTON */}
                    <button
                        type="submit"
                        disabled={isSubmitting || !description.trim()}
                        className="btn"
                        style={{ padding: '15px', fontSize: '16px', marginTop: '10px', justifyContent: 'center', opacity: (isSubmitting || !description.trim()) ? 0.5 : 1 }}
                    >
                        {isSubmitting ? 'Sending...' : (
                            <>
                                <Send size={20} />
                                Submit Report
                            </>
                        )}
                    </button>

                </form>
            </div>
        </div>
    );
}
